import { create } from "zustand";
import type { AdventureMap, CreatePlaceData, Place, PlaceGeoJSON, UpdateMapData, UpdatePlaceData } from "./api";
import * as api from "./api";
import { emptyPlacesGeoJSON } from "./mapConstants";

interface AdventureMapState {
  /** The adventure map being viewed/edited */
  map: AdventureMap | null;

  /** Places on this adventure map (as GeoJSON) */
  places: PlaceGeoJSON;

  /** Loading state */
  loading: boolean;

  /** Error message */
  error: string | null;

  /** Load an adventure map and its places by ID */
  load: (mapId: string) => Promise<void>;

  /** Update adventure map metadata */
  update: (mapId: string, data: UpdateMapData) => Promise<void>;

  /** Create a place and add it to the map */
  createAndAddToMap: (mapId: string, data: CreatePlaceData) => Promise<Place>;

  /** Add an existing global place to the map */
  addPlaceToMap: (mapId: string, placeId: string) => Promise<void>;

  /** Update a place on the map */
  updatePlace: (mapId: string, placeId: string, data: UpdatePlaceData) => Promise<void>;

  /** Delete a place from the map */
  deletePlace: (mapId: string, placeId: string) => Promise<void>;

  /** Publish a local place to noma.to (returns the new global place) */
  publishPlace: (mapId: string, placeId: string) => Promise<Place>;

  /** Reload places only (lighter than full load) */
  reloadPlaces: (mapId: string) => Promise<void>;

  /** Patch map in local state (no API call) */
  patch: (partial: Partial<AdventureMap>) => void;

  /** Reset store state */
  reset: () => void;
}

export const useAdventureMap = create<AdventureMapState>((set) => ({
  map: null,
  places: emptyPlacesGeoJSON,
  loading: false,
  error: null,

  load: async (mapId) => {
    set({ loading: true, error: null });
    try {
      const [map, places] = await Promise.all([api.getMapById(mapId), api.getGeoJsonPlacesByAdventureMapId(mapId)]);
      set({ map, places, loading: false });
    } catch (err) {
      set({ error: "Failed to load adventure map", loading: false });
      throw err;
    }
  },

  update: async (mapId, data) => {
    await api.updateMap(mapId, data);
    const map = await api.getMapById(mapId);
    set({ map });
  },

  createAndAddToMap: async (mapId, data) => {
    const place = await api.createAndAddToMap(mapId, data);
    const places = await api.getGeoJsonPlacesByAdventureMapId(mapId);
    set({ places });
    return place;
  },

  addPlaceToMap: async (mapId, placeId) => {
    await api.addPlaceToMap(mapId, placeId);
    const places = await api.getGeoJsonPlacesByAdventureMapId(mapId);
    set({ places });
  },

  updatePlace: async (mapId, placeId, data) => {
    await api.updatePlace(placeId, data);
    const places = await api.getGeoJsonPlacesByAdventureMapId(mapId);
    set({ places });
  },

  deletePlace: async (mapId, placeId) => {
    await api.deletePlace(mapId, placeId);
    const places = await api.getGeoJsonPlacesByAdventureMapId(mapId);
    set({ places });
  },

  publishPlace: async (mapId, placeId) => {
    const published = await api.publishPlace(placeId);
    const places = await api.getGeoJsonPlacesByAdventureMapId(mapId);
    set({ places });
    return published;
  },

  reloadPlaces: async (mapId) => {
    const places = await api.getGeoJsonPlacesByAdventureMapId(mapId);
    set({ places });
  },

  patch: (partial) => {
    set((state) => ({
      map: state.map ? { ...state.map, ...partial } : null,
    }));
  },

  reset: () => {
    set({
      map: null,
      places: emptyPlacesGeoJSON,
      loading: false,
      error: null,
    });
  },
}));
